import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./styles/global.css";
import "./styles/chat.css";
import "./styles/input.css";
import { App } from "./App";
import { GpEntry } from "./components/GpEntry";
import { resolveRoute } from "./status/route";
import { StatusPage } from "./status/StatusPage";
import { IncidentPage, IncidentsPage } from "./status/IncidentPage";
import { initAnalytics, installLeaveTracking } from "./analytics/posthog";

const route = resolveRoute();
const isStatusRoute = route.name !== "app";

// The status pages carry no analytics. They are the page someone loads when the
// service is failing them, and instrumenting that is not a trade worth making:
// it adds a third-party request to the one page that must render when things
// are broken, and it means observing who is checking on a health service.
if (!isStatusRoute) {
  // Consent-gated analytics (PECR): inert without VITE_POSTHOG_KEY. The default
  // tier is cookieless/anonymous; accepting cookies upgrades persistence.
  initAnalytics();
  // Exit-mode-agnostic drop-off: a `flow_left` event on page-hide (tab close /
  // navigate away / mobile background) carrying the node the resident was on and
  // whether they'd completed — covers every exit; silent walk-aways stay covered
  // by the last question_viewed with no matching question_answered.
  installLeaveTracking();
}

// Precedence: a GP token beats every other route (it is a single-use link a
// resident was texted), then the status pages, then the chat. Root only ever
// runs when there is no GP token — see the render call below.
function Root() {
  switch (route.name) {
    case "status":
      return <StatusPage />;
    case "incidents":
      return <IncidentsPage />;
    case "incident":
      return <IncidentPage reference={route.reference} />;
    default:
      return <App />;
  }
}

// Conservative defaults for a triage flow: retry transient failures with
// backoff, never silently refetch under the resident mid-conversation.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 2, refetchOnWindowFocus: false, staleTime: Infinity },
    mutations: { retry: 2 },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

// The only route besides the chat: the GP-pathway SMS link lands on
// <base>/gp/<token> (token: url-safe base64 from secrets.token_urlsafe). A
// full router would be overkill for two destinations — match the path once at
// boot; GpEntry hands over to the base (the normal chat) after the identity
// gate. Base-aware: production serves under /llr/, preview at /. The bare
// /gp/<token> form is also accepted so links minted before the /llr move
// keep working (the SPA fallback serves the app for any path).
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const gpPath = window.location.pathname.startsWith(BASE)
  ? window.location.pathname.slice(BASE.length)
  : window.location.pathname;
const gpToken = gpPath.match(/^\/gp\/([A-Za-z0-9_-]+)\/?$/)?.[1];

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {gpToken ? <GpEntry token={gpToken} /> : <Root />}
    </QueryClientProvider>
  </StrictMode>,
);
